import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, ClipboardList, Edit, Loader2, Plus, Radio, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { adminApi } from '../../utils/admin-api';
import { sessionStorage } from '../../utils/auth-api';
import {
  batchApi, SECTION_LABELS, SOURCE_LABELS,
  type BatchCatalogDevice, type BatchField, type BatchSections, type BatchStartMode, type BatchTagRef, type BatchTemplate,
} from '../../utils/batches';

const selectCls = 'h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const BYTES_PER_READING = 130; // row plus its share of the indexes, measured roughly
const ALL_ON: BatchSections = { summary: true, charts: true, dataTable: true, ioEvents: true, signatureLines: true };
const START_MODES: { id: BatchStartMode; label: string; hint: string }[] = [
  { id: 'manual', label: 'Operator button', hint: 'The operator presses Start and Complete on the portal.' },
  { id: 'trigger', label: 'Automatic signal', hint: 'A signal starts the batch when it turns on and ends it when it turns off.' },
  { id: 'both', label: 'Both', hint: 'Either way. A batch started by the button is ended by the button.' },
];

const refKey = (t: { sourceType: string; deviceId: string; tagKey: string }) => `${t.sourceType}|${t.deviceId}|${t.tagKey}`;
const browserTimeZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata'; } catch { return 'Asia/Kolkata'; }
};
const allTimeZones = (): string[] => {
  try { return (Intl as any).supportedValuesOf('timeZone'); } catch { return ['Asia/Kolkata', 'Asia/Dubai', 'Europe/London', 'America/New_York', 'UTC']; }
};

type Form = {
  name: string; description: string; kind: 'batch' | 'process'; isActive: boolean;
  startMode: BatchStartMode; triggerKey: string; threshold: string; holdSec: string;
  numberPrefix: string; product: string; fields: BatchField[];
  sampleSec: string; timeZone: string; sections: BatchSections; tags: Record<string, BatchTagRef>;
};

const emptyForm = (): Form => ({
  name: '', description: '', kind: 'batch', isActive: true,
  startMode: 'manual', triggerKey: '', threshold: '0.5', holdSec: '2',
  numberPrefix: 'BATCH', product: '', fields: [],
  sampleSec: '5', timeZone: browserTimeZone(), sections: { ...ALL_ON }, tags: {},
});

const formatSize = (bytes: number) => (bytes >= 1024 ** 3 ? `${(bytes / 1024 ** 3).toFixed(1)} GB` : `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`);

export function BatchReportsTab() {
  const token = sessionStorage.get()?.token || '';
  const [clients, setClients] = useState<any[]>([]);
  const [clientId, setClientId] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [templates, setTemplates] = useState<BatchTemplate[]>([]);
  const [catalog, setCatalog] = useState<BatchCatalogDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingEnabled, setSavingEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<BatchTemplate | null>(null);
  const [form, setForm] = useState<Form>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<BatchTemplate | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const timeZones = useMemo(allTimeZones, []);

  useEffect(() => {
    (async () => {
      try {
        const list = await adminApi.getClients(token);
        setClients(list);
        if (list.length) setClientId(list[0].id); else setLoading(false);
      } catch (e: any) {
        toast.error(e.message || 'Failed to load clients');
        setLoading(false);
      }
    })();
  }, []);

  const load = async (id = clientId) => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await batchApi.setup(token, id);
      setEnabled(data.enabled);
      setTemplates(data.templates);
      setCatalog(data.catalog);
    } catch (e: any) {
      toast.error(e.message);
      setTemplates([]);
      setCatalog([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (clientId) load(clientId); }, [clientId]);

  const toggleEnabled = async (value: boolean) => {
    setSavingEnabled(true);
    try {
      await batchApi.setEnabled(token, clientId, value);
      setEnabled(value);
      toast.success(value ? 'Batch reports switched on for this client' : 'Batch reports switched off for this client', { description: 'The client screen updates within about 30 seconds.' });
    } catch (e: any) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSavingEnabled(false);
    }
  };

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setExpanded({}); setOpen(true); };
  const openEdit = (t: BatchTemplate) => {
    setEditing(t);
    setForm({
      name: t.name, description: t.description || '', kind: t.kind, isActive: t.isActive,
      startMode: t.startMode || 'manual', triggerKey: t.trigger ? refKey(t.trigger) : '',
      threshold: String(t.trigger?.threshold ?? 0.5), holdSec: String(t.trigger?.holdSec ?? 2),
      numberPrefix: t.numberPrefix || 'BATCH', product: t.product || '', fields: t.fields || [],
      sampleSec: String(t.sampleSec || 5), timeZone: t.timeZone || browserTimeZone(),
      sections: { ...ALL_ON, ...(t.sections || {}) },
      tags: Object.fromEntries(t.tags.map((r) => [refKey(r), r])),
    });
    setExpanded({});
    setOpen(true);
  };

  const refOf = (d: BatchCatalogDevice, tag: BatchCatalogDevice['tags'][number]): BatchTagRef => ({
    sourceType: d.sourceType, deviceId: d.deviceId, deviceName: d.deviceName, tagKey: tag.tagKey, tagName: tag.tagName, unit: tag.unit, decimals: tag.decimals,
  });
  const setTag = (d: BatchCatalogDevice, tag: BatchCatalogDevice['tags'][number], on: boolean) => setForm((f) => {
    const tags = { ...f.tags };
    const ref = refOf(d, tag);
    if (on) tags[refKey(ref)] = ref; else delete tags[refKey(ref)];
    return { ...f, tags };
  });
  const setDevice = (d: BatchCatalogDevice, on: boolean) => setForm((f) => {
    const tags = { ...f.tags };
    for (const tag of d.tags) {
      const ref = refOf(d, tag);
      if (on) tags[refKey(ref)] = ref; else delete tags[refKey(ref)];
    }
    return { ...f, tags };
  });

  // Saved values whose device or tag has since been removed from the client stay listed so they can be dropped.
  const catalogKeys = useMemo(() => new Set(catalog.flatMap((d) => d.tags.map((t) => refKey({ ...d, tagKey: t.tagKey })))), [catalog]);
  const orphanTags = Object.entries(form.tags).filter(([k]) => !catalogKeys.has(k));
  const selectedCount = Object.keys(form.tags).length;
  const triggerOptions = useMemo(() => catalog.flatMap((d) => d.tags.map((t) => ({ key: refKey({ ...d, tagKey: t.tagKey }), label: `${SOURCE_LABELS[d.sourceType]}: ${d.deviceName} - ${t.tagName}`, ref: refOf(d, t) }))), [catalog]);

  // Digital inputs are saved only when they change, so they hardly count towards storage.
  const analogCount = Object.values(form.tags).filter((t) => t.sourceType !== 'io').length;
  const dailyBytes = (analogCount * 86400 * BYTES_PER_READING) / Math.max(1, Number(form.sampleSec) || 5);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Enter a template name'); return; }
    if (selectedCount === 0) { toast.error('Choose at least one value for the report'); return; }
    const payload: Record<string, any> = {
      name: form.name.trim(), description: form.description.trim(), kind: form.kind, isActive: form.isActive,
      sampleSec: Number(form.sampleSec) || 5, timeZone: form.timeZone.trim(), sections: form.sections, tags: Object.values(form.tags),
    };
    if (form.kind === 'batch') {
      Object.assign(payload, { startMode: form.startMode, numberPrefix: form.numberPrefix.trim(), product: form.product.trim(), fields: form.fields.filter((f) => f.label.trim()) });
      if (form.startMode !== 'manual') {
        const existing = editing?.trigger && refKey(editing.trigger) === form.triggerKey ? editing.trigger : null;
        const ref = triggerOptions.find((o) => o.key === form.triggerKey)?.ref || existing;
        if (!ref) { toast.error('Choose the signal that starts and stops the batch'); return; }
        payload.trigger = { ...ref, threshold: Number(form.threshold), holdSec: Number(form.holdSec) };
      }
    }
    setSaving(true);
    try {
      if (editing) await batchApi.updateTemplate(token, clientId, editing.id, payload as Partial<BatchTemplate>);
      else await batchApi.createTemplate(token, clientId, payload as Partial<BatchTemplate>);
      toast.success(editing ? 'Template updated' : 'Template created');
      setOpen(false);
      load();
    } catch (e: any) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (t: BatchTemplate) => {
    try {
      await batchApi.updateTemplate(token, clientId, t.id, { ...t, isActive: !t.isActive });
      load();
    } catch (e: any) { toast.error(e.message); }
  };
  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await batchApi.deleteTemplate(token, clientId, deleting.id);
      toast.success(`Deleted ${deleting.name}`);
      setDeleting(null);
      load();
    } catch (e: any) { toast.error(e.message); }
  };

  const startModeLabel = (t: BatchTemplate) => START_MODES.find((m) => m.id === (t.startMode || 'manual'))?.label;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold"><ClipboardList className="h-6 w-6" /> Batch Reports</h2>
          <p className="text-sm text-muted-foreground">Create batch and process report templates for the clients who need them. Clients see only the templates you make here.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <select className={`${selectCls} sm:w-[220px]`} value={clientId} onChange={(e) => setClientId(e.target.value)} aria-label="Client">
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <Button onClick={openCreate} disabled={!clientId}><Plus className="mr-2 h-4 w-4" /> Add template</Button>
        </div>
      </div>

      {clientId && !loading && (
        <div className={`flex items-center justify-between gap-4 rounded-lg border p-4 ${enabled ? 'border-emerald-500/40 bg-emerald-500/5' : ''}`}>
          <div className="min-w-0">
            <Label htmlFor="batch-enabled" className="font-semibold">Batch reports for this client</Label>
            <p className="mt-1 text-xs text-muted-foreground">
              {enabled
                ? 'On. The client has a Batch Reports screen, and values used by these templates are being saved.'
                : 'Off. The client does not see the screen and nothing extra is saved. You can prepare templates first and switch this on when ready.'}
            </p>
          </div>
          <Switch id="batch-enabled" checked={enabled} disabled={savingEnabled} onCheckedChange={toggleEnabled} />
        </div>
      )}

      {loading ? (
        <div className="flex h-48 items-center justify-center text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…</div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
            <ClipboardList className="h-12 w-12 text-muted-foreground opacity-50" />
            <div className="text-lg font-semibold">No templates for this client</div>
            <p className="max-w-md text-sm text-muted-foreground">A batch template records between a start and a stop. A process template reports on any time range you pick.</p>
            <Button onClick={openCreate} disabled={!clientId}><Plus className="mr-2 h-4 w-4" /> Add the first template</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {templates.map((t) => (
            <Card key={t.id} className={t.isActive ? '' : 'opacity-60'}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="break-words text-lg">{t.name}</CardTitle>
                    <CardDescription>{t.kind === 'batch' ? 'Batch report' : 'Process report'}</CardDescription>
                  </div>
                  <Switch checked={t.isActive} onCheckedChange={() => toggleActive(t)} aria-label={`${t.name} active`} />
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2 text-xs">
                  {t.kind === 'batch' && <span className="rounded-md bg-muted px-2 py-1">{startModeLabel(t)}</span>}
                  <span className="rounded-md bg-muted px-2 py-1">{t.tags.length} value{t.tags.length === 1 ? '' : 's'}</span>
                  <span className="rounded-md bg-muted px-2 py-1">every {t.sampleSec} s</span>
                  <span className="rounded-md bg-muted px-2 py-1">{t.timeZone}</span>
                  {t.kind === 'process' && <span className="rounded-md bg-amber-500/10 px-2 py-1 text-amber-600 dark:text-amber-400">Saves data all the time</span>}
                </div>
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(t)}><Edit className="mr-1.5 h-4 w-4" /> Edit</Button>
                  <Button variant="outline" size="sm" onClick={() => setDeleting(t)} aria-label={`Delete ${t.name}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => !saving && setOpen(o)}>
        <DialogContent className="max-h-[92vh] w-[96vw] overflow-y-auto sm:max-w-[900px] [&>*]:min-w-0">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add report template'}</DialogTitle>
            <DialogDescription>Batches already started keep the template they started with, so editing never changes an existing report.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5"><Label htmlFor="batch-name">Template name *</Label><Input id="batch-name" value={form.name} maxLength={120} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Reactor 1 batch" /></div>
              <div className="space-y-1.5">
                <Label htmlFor="batch-kind">Report type</Label>
                <select id="batch-kind" className={selectCls} value={form.kind} onChange={(e) => set('kind', e.target.value as Form['kind'])}>
                  <option value="batch">Batch report (between a start and a stop)</option>
                  <option value="process">Process report (any time range)</option>
                </select>
              </div>
              <div className="space-y-1.5 md:col-span-2"><Label htmlFor="batch-description">Description</Label><Input id="batch-description" value={form.description} maxLength={500} onChange={(e) => set('description', e.target.value)} placeholder="Optional, shown to the client" /></div>
            </div>

            {form.kind === 'batch' && (
              <div className="space-y-4 rounded-lg border p-4">
                <div className="text-sm font-semibold">How the batch starts and stops</div>
                <div className="grid gap-2 sm:grid-cols-3">
                  {START_MODES.map((m) => (
                    <button key={m.id} type="button" aria-pressed={form.startMode === m.id} onClick={() => set('startMode', m.id)}
                      className={`rounded-lg border p-3 text-left transition-colors ${form.startMode === m.id ? 'border-primary bg-primary/10' : 'hover:bg-muted'}`}>
                      <div className="text-sm font-semibold">{m.label}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">{m.hint}</div>
                    </button>
                  ))}
                </div>

                {form.startMode !== 'manual' && (
                  <div className="grid gap-3 rounded-md bg-muted/40 p-3 md:grid-cols-[1fr_120px_120px]">
                    <div className="space-y-1.5">
                      <Label htmlFor="batch-signal" className="flex items-center gap-1.5"><Radio className="h-3.5 w-3.5" /> Signal *</Label>
                      <select id="batch-signal" className={selectCls} value={form.triggerKey} onChange={(e) => set('triggerKey', e.target.value)}>
                        <option value="">Choose a PLC tag, input or value…</option>
                        {editing?.trigger && !triggerOptions.some((o) => o.key === refKey(editing.trigger!)) && (
                          <option value={refKey(editing.trigger)}>{editing.trigger.deviceName} - {editing.trigger.tagName} (no longer configured)</option>
                        )}
                        {triggerOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1.5"><Label htmlFor="batch-threshold">On above</Label><Input id="batch-threshold" type="number" step="any" value={form.threshold} onChange={(e) => set('threshold', e.target.value)} /></div>
                    <div className="space-y-1.5"><Label htmlFor="batch-hold">Hold (seconds)</Label><Input id="batch-hold" type="number" min={0} max={300} value={form.holdSec} onChange={(e) => set('holdSec', e.target.value)} /></div>
                    <p className="text-xs text-muted-foreground md:col-span-3">
                      On means the value is above "On above" (0.5 suits an ON/OFF signal). The signal must stay changed for the hold time, so a short flicker does not start or end a batch. If the signal is already on when the API restarts, nothing starts until it next turns off and on.
                    </p>
                  </div>
                )}

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5"><Label htmlFor="batch-prefix">Batch number prefix</Label><Input id="batch-prefix" value={form.numberPrefix} maxLength={20} onChange={(e) => set('numberPrefix', e.target.value)} /><p className="text-xs text-muted-foreground">Automatic numbers look like {form.numberPrefix || 'BATCH'}-20261102-001.</p></div>
                  <div className="space-y-1.5"><Label htmlFor="batch-product">Default product</Label><Input id="batch-product" value={form.product} maxLength={120} onChange={(e) => set('product', e.target.value)} placeholder="Optional" /></div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label>Details the operator fills in</Label>
                    <Button type="button" variant="outline" size="sm" disabled={form.fields.length >= 10} onClick={() => set('fields', [...form.fields, { label: '', required: false }])}><Plus className="mr-1 h-3.5 w-3.5" /> Add</Button>
                  </div>
                  {form.fields.length === 0 && <p className="text-xs text-muted-foreground">For example Lot number, Recipe or Shift. They appear at the top of the report.</p>}
                  {form.fields.map((f, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input className="h-8 text-sm" value={f.label} maxLength={60} placeholder="Name of the detail" aria-label={`Detail ${i + 1} name`}
                        onChange={(e) => set('fields', form.fields.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
                      <label className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                        <Switch checked={f.required} onCheckedChange={(v) => set('fields', form.fields.map((x, j) => (j === i ? { ...x, required: v } : x)))} aria-label={`Detail ${i + 1} required`} />
                        Required
                      </label>
                      <button type="button" className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label={`Remove detail ${i + 1}`} onClick={() => set('fields', form.fields.filter((_, j) => j !== i))}><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-3 rounded-lg border p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-semibold">Values in the report <span className="font-normal text-muted-foreground">· {selectedCount} chosen</span></div>
              </div>
              {catalog.length === 0 && <p className="text-sm text-muted-foreground">This client has no devices with values yet.</p>}
              <div className="space-y-1.5">
                {catalog.map((d) => {
                  const key = `${d.sourceType}|${d.deviceId}`;
                  const chosen = d.tags.filter((t) => form.tags[refKey({ ...d, tagKey: t.tagKey })]).length;
                  const isOpen = !!expanded[key];
                  return (
                    <div key={key} className="rounded-md border">
                      <div className="flex items-center gap-2 p-2">
                        <button type="button" aria-expanded={isOpen} aria-label={`Show values of ${d.deviceName}`} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" onClick={() => setExpanded((x) => ({ ...x, [key]: !isOpen }))}>
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                        <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setExpanded((x) => ({ ...x, [key]: !isOpen }))}>
                          <span className="break-words text-sm font-medium">{d.deviceName}</span>
                          <span className="ml-2 text-xs text-muted-foreground">{SOURCE_LABELS[d.sourceType]}</span>
                        </button>
                        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{chosen}/{d.tags.length}</span>
                        <Button type="button" variant="ghost" size="sm" className="h-7 shrink-0 px-2 text-xs" onClick={() => setDevice(d, chosen < d.tags.length)}>{chosen < d.tags.length ? 'All' : 'None'}</Button>
                      </div>
                      {isOpen && (
                        <div className="grid gap-1.5 border-t p-3 sm:grid-cols-2 lg:grid-cols-3">
                          {d.tags.map((t) => {
                            const id = `batch-tag-${refKey({ ...d, tagKey: t.tagKey })}`;
                            return (
                              <div key={t.tagKey} className="flex min-w-0 items-center gap-2">
                                <Checkbox id={id} checked={!!form.tags[refKey({ ...d, tagKey: t.tagKey })]} onCheckedChange={(c) => setTag(d, t, c === true)} />
                                <Label htmlFor={id} className="min-w-0 cursor-pointer break-words text-sm font-normal">{t.tagName}{t.unit ? ` (${t.unit})` : ''}</Label>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                {orphanTags.length > 0 && (
                  <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
                    <div className="mb-1.5 text-xs font-semibold text-amber-600 dark:text-amber-400">No longer configured on this client</div>
                    <div className="flex flex-wrap gap-2">
                      {orphanTags.map(([k, t]) => (
                        <button key={k} type="button" className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs hover:bg-muted/70"
                          onClick={() => setForm((f) => { const tags = { ...f.tags }; delete tags[k]; return { ...f, tags }; })}>
                          {t.deviceName} - {t.tagName} <X className="h-3 w-3" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="grid gap-4 rounded-lg border p-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="batch-sample">Save a reading every (seconds)</Label>
                <Input id="batch-sample" type="number" min={1} max={300} value={form.sampleSec} onChange={(e) => set('sampleSec', e.target.value)} />
                <p className="text-xs text-muted-foreground">
                  {analogCount === 0 ? 'Digital inputs are saved only when they change.'
                    : `About ${formatSize(dailyBytes)} of database space per day ${form.kind === 'process' ? '(this template saves all the time)' : 'of batch running time'}.`}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="batch-tz">Time zone printed on the report</Label>
                <Input id="batch-tz" list="batch-tz-list" value={form.timeZone} onChange={(e) => set('timeZone', e.target.value)} />
                <datalist id="batch-tz-list">{timeZones.map((z) => <option key={z} value={z} />)}</datalist>
                <p className="text-xs text-muted-foreground">Use the plant's time zone, for example Asia/Kolkata.</p>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Report contents</Label>
                <div className="grid gap-2 sm:grid-cols-2">
                  {(Object.keys(SECTION_LABELS) as (keyof BatchSections)[]).filter((k) => form.kind === 'batch' || k !== 'signatureLines').map((k) => (
                    <div key={k} className="flex items-center gap-2">
                      <Checkbox id={`batch-section-${k}`} checked={form.sections[k]} onCheckedChange={(c) => set('sections', { ...form.sections, [k]: c === true })} />
                      <Label htmlFor={`batch-section-${k}`} className="cursor-pointer text-sm font-normal">{SECTION_LABELS[k]}</Label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3 md:col-span-2"><Switch id="batch-active" checked={form.isActive} onCheckedChange={(v) => set('isActive', v)} /><Label htmlFor="batch-active">Active (shown to the client)</Label></div>
            </div>
          </div>

          <div className="flex justify-between gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editing ? 'Save changes' : 'Create template'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleting?.name}?</DialogTitle>
            <DialogDescription>The client can no longer run this template. Batches already recorded and their reports are kept.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}><Trash2 className="mr-2 h-4 w-4" /> Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
